import { makeAutoObservable, reaction, runInAction, type IReactionDisposer } from 'mobx'
import { RequestLoadData } from 'shared/api/lib'
import { type IParamsGetCannedMessages, type IResponseCannedMessage } from 'entities/CannedMessages'
import { CannedMessagesApi } from 'entities/CannedMessages/api/savedReplies'

type Props = {
  filter: 'favorite' | 'unfavorite'
  setSavedReplay: (savedReplayResponse: IResponseCannedMessage) => void
}

export class ListSavedRepliesStore {
  private _disposeReactionLoadData: IReactionDisposer | null = null
  private _filter: 'favorite' | 'unfavorite' = 'unfavorite'
  private _setSavedReplay: ((savedReplayResponse: IResponseCannedMessage) => void) | null
  private _page = 1
  private _last_page = 0
  private _length = 20
  private _parentParams: IParamsGetCannedMessages = {}

  constructor({ filter, setSavedReplay }: Props) {
    this._filter = filter
    this._setSavedReplay = setSavedReplay
    this.reactionLoadData()

    makeAutoObservable(this)
  }

  get loading() {
    return this.requestLoadData.loading
  }

  get paramsGetItems(): IParamsGetCannedMessages {
    return {
      ...this._parentParams,
      page: this._page,
      length: this._length,
      filter: this._filter,
    }
  }

  get hasMore() {
    return this._page <= this._last_page
  }

  reset = () => {
    this._disposeReactionLoadData?.()
    this.clear()
  }

  clear = () => {
    this.requestLoadData.setLoading(false)
    this._last_page = 0
    this._page = 1
  }

  readonly requestLoadData = new RequestLoadData<IParamsGetCannedMessages>({
    requestCallback: async ({ cancelRequest }, params) => {
      if (params) this._parentParams = params
      if (this._page > 1 && !this.hasMore) return

      const { data } = await CannedMessagesApi.getCannedMessages(this.paramsGetItems, {
        cancelToken: cancelRequest.token,
      })

      runInAction(() => {
        this._last_page = data.last_page
      })

      if (this._setSavedReplay && data) data.data.forEach(this._setSavedReplay)
    },
  })

  loadData = async (params?: IParamsGetCannedMessages) => {
    return this.requestLoadData.loadData(params)
  }

  loadMore = () => {
    if (this.loading) return

    this._page = this._page + 1
    this.requestLoadData.setLoading(true)
  }

  reactionLoadData = () => {
    this._disposeReactionLoadData?.()
    this._disposeReactionLoadData = reaction(
      () => this._page,
      () => this.requestLoadData.debounceLoadData()
    )
  }
}
